import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, getV2IndexHtml } from '@/api/client';
import { useNotificationConnection } from '@/stores/notificationConnection';
import { Icon } from '@/components/Icon';
import s from './AboutPage.module.css';

type Info = { appName: string; passwordMinLength: number };

export function AboutPage() {
  const navigate = useNavigate();
  const [appName, setAppName] = useState('贸易商进销存系统');
  const [online, setOnline] = useState(navigator.onLine);
  const [push, setPush] = useState('查询中…');
  const [copyStatus, setCopyStatus] = useState('');
  const [updateStatus, setUpdateStatus] = useState('');
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const realtimeConnected = useNotificationConnection((state) => state.connected);
  const standalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const runMode = standalone ? '主屏幕应用' : /Safari/i.test(navigator.userAgent) && !/Chrome|CriOS|Edg/i.test(navigator.userAgent) ? 'Safari 网页' : '浏览器网页';

  useEffect(() => {
    let active = true;
    const refresh = () => { void api.get<Info>('/api/v2/me/settings').then((info) => { if (active) setAppName(info.appName); }).catch(() => undefined); };
    refresh();
    const timer = window.setInterval(refresh, 30_000);
    window.addEventListener('focus', refresh);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, []);
  useEffect(() => {
    const refresh = () => setOnline(navigator.onLine);
    window.addEventListener('online', refresh);
    window.addEventListener('offline', refresh);
    return () => { window.removeEventListener('online', refresh); window.removeEventListener('offline', refresh); };
  }, []);
  useEffect(() => {
    let active = true;
    const inspect = async () => {
      if (!('Notification' in window) || !('serviceWorker' in navigator)) { if (active) setPush('此浏览器不支持'); return; }
      if (Notification.permission !== 'granted') { if (active) setPush('未开启'); return; }
      try {
        const registration = await navigator.serviceWorker.getRegistration('/');
        const script = registration?.active?.scriptURL || registration?.waiting?.scriptURL || registration?.installing?.scriptURL;
        if (!registration || !script || new URL(script).pathname !== '/sw.js' || new URL(registration.scope).pathname !== '/') { if (active) setPush('未开启'); return; }
        const subscription = await registration.pushManager?.getSubscription();
        if (active) setPush(subscription ? '已开启' : '未开启');
      } catch { if (active) setPush('状态不可用'); }
    };
    void inspect();
    return () => { active = false; };
  }, []);

  const copy = async () => {
    const lines = [
      'V2 诊断信息',
      '版本：2.0.0',
      `运行方式：${runMode}`,
      `网络：${online ? '在线' : '离线'}`,
      `实时提醒：${realtimeConnected ? '已连接' : '未连接'}`,
      `后台推送：${push}`,
      `设备：${navigator.platform || '未知'}`,
    ];
    try { await navigator.clipboard.writeText(lines.join('\n')); setCopyStatus('已复制诊断信息'); }
    catch { setCopyStatus('复制失败，请检查剪贴板权限'); }
  };

  const realtimeStatus = !online ? '离线' : realtimeConnected ? '已连接' : '未连接';
  const pushTone = push === '已开启' ? s.green : push === '未开启' ? s.amber : s.grey;

  const checkUpdate = async () => {
    if (updateAvailable) { window.location.reload(); return; }
    setCheckingUpdate(true);
    setUpdateStatus('正在检查…');
    try {
      const html = new DOMParser().parseFromString(await getV2IndexHtml(), 'text/html');
      const assets = (root: Document) => [...root.querySelectorAll('script[src], link[rel="stylesheet"][href]')]
        .map((node) => node.getAttribute('src') || node.getAttribute('href') || '')
        .filter((value) => value.includes('/v2assets/'))
        .sort().join('|');
      const current = assets(document);
      const latest = assets(html);
      if (!current || !latest) {
        if (import.meta.env.DEV && !current && !latest) { setUpdateStatus('已是最新'); return; }
        setUpdateStatus('当前环境无法比较静态资源版本'); return;
      }
      const changed = current !== latest;
      setUpdateAvailable(changed);
      setUpdateStatus(changed ? '发现新版本，点此刷新' : '已是最新');
    } catch { setUpdateStatus('检查失败，请稍后重试'); }
    finally { setCheckingUpdate(false); }
  };

  return <div className={s.page}>
    <header className={s.header}><button type="button" onClick={() => navigate('/me')}>‹ 我的</button><h1>关于</h1><span /></header>
    <main className={s.body}>
      <section className={s.hero}><span className={s.brand}>发</span><h2>{appName}</h2><p>版本 2.0.0 · {__V2_BUILD_DATE__} 构建</p></section>
      <div className={s.group}>
        <div><span>运行方式</span><b>{runMode}</b></div>
        <div><span>实时提醒</span><b><span className={`${s.dot} ${online && realtimeConnected ? s.green : online ? s.amber : s.red}`}>{realtimeStatus}</span></b></div>
        <div><span>后台推送</span><b><span className={`${s.dot} ${pushTone}`}>{push}{!standalone && push === '未开启' ? ' · 需添加到主屏幕' : ''}</span></b></div>
        <div><span>本机保存</span><b><span>外观偏好、未提交的草稿</span><small>业务数据不存在手机上</small></b></div>
      </div>
      <div className={s.actions}>
        <button type="button" disabled={checkingUpdate} onClick={() => void checkUpdate()}>
          <span className={s.actionLabel}>检查更新</span>
          <span className={`${s.actionEnd} ${updateStatus === '已是最新' ? s.greenText : ''}`} aria-live="polite">{updateStatus || '点击检查'}</span>
          <Icon name="chev" size={14} className={s.actionIcon} />
        </button>
        <button type="button" onClick={() => void copy()}>
          <span className={s.copyLabel}>复制诊断信息<small>版本、设备、连接状态，发给管理员排查用；不含业务数据</small></span>
          {copyStatus && <span className={s.copyStatus}>{copyStatus}</span>}
          <Icon name="copy" size={14} className={s.actionIcon} />
        </button>
      </div>
      <p className={s.help}>系统名称由管理员在「设置」里修改，改完这里和登录页一起变，不用刷新。</p>
      {copyStatus && <p className={s.status} role="status" aria-live="polite">{copyStatus}</p>}
    </main>
  </div>;
}
